import { APIError, ValidationMessage } from '@tlx/astro-shared';
import { SalaryOnboardingStageForm } from '@Page/Onboarding/SalaryOnboardingStage';
import { OnboardingStageSkeleton } from '@Page/Onboarding/components/OnboardingStageSkeleton';
import { updateSalarySettings, useSalarySettings } from '@Page/Onboarding/hooks/useSalarySettings';
import { setCustomValidityOnField } from '@Page/Onboarding/components/ValidatedForm/setCustomValidityOnField';
import { FormattedMessage, useIntl } from 'react-intl';
import { JumboToggle } from '@tlx/beehive-shared';
import { useState } from 'react';
import { createOrdinalFromArray } from '@Util/misc.ts';

type ActivateFunctionsStageValues = {
    isFreeCar: boolean;
    isUnionDeduction: boolean;
};

export function ConnectedActivateFunctionsStage() {
    const { data, isLoading } = useSalarySettings();
    if (!data || isLoading) {
        return <OnboardingStageSkeleton />;
    }

    return (
        <ActivateFunctionsStage
            handleSubmit={async (formData) => {
                /*
                doing this because form data is not being sent if checked is false
                so if you go from true to false, it sends in no changes
                but this forces us to always send in some value (even if it is the same as what it was)
                */
                const dto = {
                    isFreeCar: false,
                    isUnionDeduction: false,
                    ...formData,
                };
                await updateSalarySettings(dto);
            }}
            handleError={(form, error) => {
                const unhandled: ValidationMessage[] = [];
                error.details.validationMessages?.forEach((message) => {
                    if (message.field) {
                        const handled = setCustomValidityOnField(form, message.field, message.message);

                        if (!handled) {
                            unhandled.push(message);
                        }
                    }
                });
                return unhandled;
            }}
            defaultValue={data}
        />
    );
}

export function ActivateFunctionsStage({
    handleSubmit,
    handleError,
    defaultValue,
}: {
    handleSubmit?: (formData: Partial<ActivateFunctionsStageValues>) => Promise<void>;
    handleError?: (form: HTMLFormElement, error: APIError) => ValidationMessage[] | void;
    defaultValue: ActivateFunctionsStageValues;
}) {
    const intl = useIntl();

    //these useStates are only for the visual controlled checked on/off, the formData values are uncontrolled, taken using the name and value.
    const [isFreeCar, setIsFreeCar] = useState(defaultValue.isFreeCar);
    const [isUnionDeduction, setIsUnionDeduction] = useState(defaultValue.isUnionDeduction);
    return (
        <SalaryOnboardingStageForm
            title={intl.formatMessage({
                id: 'tsk_onboarding_activate_feature_title',
            })}
            description={intl.formatMessage({
                id: 'tsk_onboarding_activate_feature_description',
            })}
            handleSubmit={handleSubmit}
            handleError={handleError}
        >
            <JumboToggle
                name="isFreeCar"
                value="true"
                checked={isFreeCar}
                data-testid="free-car-toggle"
                className="atl-justify-between"
                onClick={() => setIsFreeCar((prevState) => !prevState)}
            >
                <div>
                    <div className="atl-text-xl atl-font-medium">
                        <FormattedMessage id={'th_company_car2'} />
                    </div>
                    <div>
                        <FormattedMessage
                            id={'tsk_onboarding_activate_feature_prompt'}
                            values={createOrdinalFromArray([
                                intl.formatMessage({ id: 'th_company_car2' }).toLowerCase(),
                            ])}
                        />
                    </div>
                </div>
            </JumboToggle>
            <JumboToggle
                name="isUnionDeduction"
                value="true"
                data-testid="union-deduction-toggle"
                className="atl-justify-between"
                checked={isUnionDeduction}
                onClick={() => setIsUnionDeduction((prevState) => !prevState)}
            >
                <div>
                    <div className="atl-text-xl atl-font-medium">
                        {intl.formatMessage({
                            id: 'text_union_deduction',
                        })}
                    </div>
                    <div>
                        <FormattedMessage
                            id={'tsk_onboarding_activate_feature_prompt'}
                            values={createOrdinalFromArray([
                                intl
                                    .formatMessage({
                                        id: 'text_union_deduction',
                                    })
                                    .toLowerCase(),
                            ])}
                        />
                    </div>
                </div>
            </JumboToggle>
        </SalaryOnboardingStageForm>
    );
}
